import { supabase } from '../lib/supabaseClient.ts';

export interface LocalAlert {
  id: string;
  alert_type: 'weather' | 'pest' | 'disease' | 'government' | 'seasonal' | 'community';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  location_coords?: { lat: number; lng: number };
  radius_km: number;
  expiry_date?: string;
  crops_affected?: string[];
  created_by?: string;
  verified_by?: string;
  action_required?: string;
  source_type: string;
  is_active: boolean;
  created_at: string;
  distance?: number; // calculated distance from user
}

class AlertService {
  // Calculate distance between two coordinates (Haversine formula)
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  // Get user's location (fallback to Maseru if not available)
  private async getUserLocation(): Promise<{ lat: number; lng: number }> {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          () => {
            // Fallback to Maseru, Lesotho
            resolve({ lat: -29.3167, lng: 27.4833 });
          },
          { timeout: 5000 }
        );
      } else {
        resolve({ lat: -29.3167, lng: 27.4833 });
      }
    });
  }

  // Fetch alerts relevant to user's location and crops
  async getLocalAlerts(userCrops: string[] = [], maxDistance: number = 25): Promise<LocalAlert[]> {
    try {
      const userLocation = await this.getUserLocation();
      
      // Generate fresh weather alerts based on current conditions
      await this.generateCurrentWeatherAlerts(userLocation);
      
      const { data: alerts, error } = await supabase
        .from('local_alerts')
        .select('*')
        .eq('is_active', true)
        .or(`expiry_date.is.null,expiry_date.gt.${new Date().toISOString()}`)
        .order('severity', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter and calculate distances
      const relevantAlerts = alerts
        .map(alert => {
          let distance = 0;
          if (alert.location_coords) {
            distance = this.calculateDistance(
              userLocation.lat,
              userLocation.lng,
              alert.location_coords.lat,
              alert.location_coords.lng
            );
          }
          return { ...alert, distance };
        })
        .filter(alert => {
          // Include if within radius or no location specified
          const withinRadius = !alert.location_coords || alert.distance <= Math.max(alert.radius_km, maxDistance);
          
          // Include if crops match or no crops specified or weather/government alerts
          const cropRelevant = !alert.crops_affected?.length || 
                              alert.alert_type === 'weather' || 
                              alert.alert_type === 'government' ||
                              userCrops.some(crop => alert.crops_affected?.includes(crop));
          
          return withinRadius && cropRelevant;
        })
        .sort((a, b) => {
          // Sort by severity first, then by distance
          const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
          if (severityDiff !== 0) return severityDiff;
          return (a.distance || 0) - (b.distance || 0);
        });

      return relevantAlerts;
    } catch (error) {
      console.error('Failed to fetch local alerts:', error);
      return this.getFallbackAlerts();
    }
  }

  // Create alert from community report
  async createAlertFromReport(reportId: string, reportData: any): Promise<void> {
    try {
      if (reportData.severity === 'critical' || reportData.severity === 'high') {
        const alertData = {
          alert_type: 'community',
          severity: reportData.severity,
          title: `${reportData.report_type.toUpperCase()}: ${reportData.crop} in ${reportData.village}`,
          description: reportData.description,
          location_coords: reportData.gps_coords,
          radius_km: reportData.severity === 'critical' ? 15 : 10,
          expiry_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          crops_affected: reportData.crop !== 'general' ? [reportData.crop] : null,
          created_by: reportData.farmer_id,
          action_required: reportData.action_taken,
          source_type: 'community_report'
        };

        const { error } = await supabase
          .from('local_alerts')
          .insert(alertData);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Failed to create alert from report:', error);
    }
  }

  // Generate weather alerts based on current weather data
  private async generateCurrentWeatherAlerts(location: { lat: number; lng: number }): Promise<void> {
    try {
      // Get current weather data from our weather service
      const { weatherService } = await import('../services/weatherService.js');
      const weatherData = await weatherService.getWeatherData('Maseru, Lesotho');
      
      const alerts = [];
      const now = new Date();

      // Frost warning (critical for Lesotho highlands)
      if (weatherData.temperature <= 2) {
        alerts.push({
          alert_type: 'weather',
          severity: weatherData.temperature <= 0 ? 'critical' : 'high',
          title: weatherData.temperature <= 0 ? 'Frost Alert - Freezing' : 'Frost Warning',
          description: `Temperature is ${weatherData.temperature}°C. ${weatherData.temperature <= 0 ? 'Immediate action required.' : 'Frost likely tonight.'}`,
          location_coords: location,
          radius_km: 25,
          expiry_date: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
          crops_affected: ['maize', 'beans', 'vegetables'],
          action_required: weatherData.temperature <= 0 ? 
            'Cover crops immediately, harvest mature vegetables' :
            'Cover sensitive crops, monitor overnight temperatures',
          source_type: 'weather_service'
        });
      }

      // High wind warning
      if (weatherData.windSpeed >= 40) {
        alerts.push({
          alert_type: 'weather',
          severity: weatherData.windSpeed >= 60 ? 'high' : 'medium',
          title: `Strong Wind ${weatherData.windSpeed >= 60 ? 'Warning' : 'Advisory'}`,
          description: `Wind speeds of ${weatherData.windSpeed} km/h. Secure equipment and structures.`,
          location_coords: location,
          radius_km: 30,
          expiry_date: new Date(now.getTime() + 12 * 60 * 60 * 1000).toISOString(),
          action_required: 'Secure loose equipment, avoid field work, check crop supports',
          source_type: 'weather_service'
        });
      }

      // High humidity disease risk
      if (weatherData.humidity >= 85 && weatherData.temperature >= 15) {
        alerts.push({
          alert_type: 'weather',
          severity: 'medium',
          title: 'High Humidity - Disease Risk',
          description: `Humidity at ${weatherData.humidity}% creates conditions for plant diseases.`,
          location_coords: location,
          radius_km: 20,
          expiry_date: new Date(now.getTime() + 48 * 60 * 60 * 1000).toISOString(),
          crops_affected: ['maize', 'beans'],
          action_required: 'Monitor for disease symptoms, improve ventilation, avoid overhead watering',
          source_type: 'weather_service'
        });
      }

      if (alerts.length > 0) {
        // Check for existing similar alerts to avoid duplicates
        const { data: existingAlerts } = await supabase
          .from('local_alerts')
          .select('title')
          .eq('alert_type', 'weather')
          .eq('is_active', true)
          .gte('created_at', new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString());

        const newAlerts = alerts.filter(alert => 
          !existingAlerts?.some(existing => existing.title === alert.title)
        );

        if (newAlerts.length > 0) {
          const { error } = await supabase
            .from('local_alerts')
            .insert(newAlerts);

          if (error) throw error;
        }
      }
    } catch (error) {
      console.error('Failed to generate weather alerts:', error);
      // Continue without weather alerts if service fails
    }
  }

  // Fallback alerts for offline/error scenarios
  private getFallbackAlerts(): LocalAlert[] {
    return [
      {
        id: 'fallback-1',
        alert_type: 'seasonal',
        severity: 'medium',
        title: 'Planting Season Reminder',
        description: 'Optimal planting time for maize is approaching. Prepare your fields and seeds.',
        radius_km: 50,
        action_required: 'Prepare fields, check seed quality, plan irrigation',
        source_type: 'system',
        is_active: true,
        created_at: new Date().toISOString(),
        distance: 0
      },
      {
        id: 'fallback-2',
        alert_type: 'pest',
        severity: 'high',
        title: 'Fall Armyworm Season',
        description: 'Fall armyworm activity typically increases during this period. Monitor young maize plants.',
        radius_km: 25,
        crops_affected: ['maize'],
        action_required: 'Scout fields regularly, prepare organic treatments',
        source_type: 'system',
        is_active: true,
        created_at: new Date().toISOString(),
        distance: 5
      }
    ];
  }

  // Mark alert as read/dismissed by user
  async markAlertAsRead(alertId: string, userId: string): Promise<void> {
    try {
      const readAlerts = JSON.parse(localStorage.getItem(`read_alerts_${userId}`) || '[]');
      if (!readAlerts.includes(alertId)) {
        readAlerts.push(alertId);
        localStorage.setItem(`read_alerts_${userId}`, JSON.stringify(readAlerts));
      }
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    }
  }

  // Get user's read alerts
  getUserReadAlerts(userId: string): string[] {
    try {
      return JSON.parse(localStorage.getItem(`read_alerts_${userId}`) || '[]');
    } catch {
      return [];
    }
  }
}

export const alertService = new AlertService();